import { useEffect, useState } from 'react';
import type { Tenant } from '../lib/database.types';

/**
 * Animation d'ouverture de la vitrine client : overlay plein écran aux couleurs de
 * marque, logo en zoom + nom du resto en fondu, puis l'overlay s'efface en douceur.
 * Joué à CHAQUE ouverture/visite (décision produit). Aucune donnée en dur : logo, nom
 * et couleurs viennent du tenant (--brand-* posées par applyTheme).
 *
 * Accessibilité : respecte `prefers-reduced-motion` (retrait immédiat, sans animation)
 * et n'intercepte aucun clic (pointer-events: none dès la sortie).
 */
export default function IntroSplash({ tenant }: { tenant: Tenant }) {
  const [phase, setPhase] = useState<'show' | 'out' | 'done'>('show');

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setPhase('done');
      return;
    }
    // Entrée logo+nom ~1,2 s, puis temps de pause à l'écran, puis sortie en fondu.
    const tOut = setTimeout(() => setPhase('out'), 2700);
    const tDone = setTimeout(() => setPhase('done'), 3400);
    return () => {
      clearTimeout(tOut);
      clearTimeout(tDone);
    };
  }, []);

  if (phase === 'done') return null;

  return (
    <div
      className={'intro-splash' + (phase === 'out' ? ' intro-splash--out' : '')}
      role="presentation"
      aria-hidden="true"
    >
      <div className="intro-splash__inner">
        {tenant.logo_url ? (
          <img src={tenant.logo_url} alt="" className="intro-splash__logo" />
        ) : (
          <span className="intro-splash__letter" style={{ background: 'var(--brand-primary)' }}>
            {tenant.name.slice(0, 1)}
          </span>
        )}
        <span className="intro-splash__name display-font">{tenant.name}</span>
        <span className="intro-splash__bar" style={{ background: 'var(--brand-primary)' }} />
      </div>
    </div>
  );
}
